import React, { useState } from 'react'
import { Linking, View } from 'react-native'
import { connect } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import classNames from 'classnames'
import { isEmpty, isNull, noop } from 'lodash-es'

import { TrackSession } from '@lyrahealth-inc/shared-app-logic/src/features/assignments/types'
import { Content } from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import DefaultButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/defaultButton/DefaultButton'
import LtIcon from '@lyrahealth-inc/ui-core/lib/lyraTherapy/assignmentsList/LtIcon'
import { ButtonType } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/baseButton/BaseButton'
import { DropdownButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/dropdownButton/DropdownButton'
import { BaseModal } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/baseModal/BaseModal'
import { FullScreenOverlay } from '@lyrahealth-inc/ui-core-crossplatform/src/organisms/fullScreenOverlay/FullScreenOverlay'
import { ActivityEntry } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/activityEntry/ActivityEntry'
import { InfoSheet } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/infoSheet/InfoSheet'
import { Lesson } from '@lyrahealth-inc/ui-core-crossplatform/src/pages/lesson/Lesson.web'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import styles from './configTrackContainer.module.scss'
import ElectiveErrorContent from './ElectiveErrorContent'
import { CLIENT_ACTIVITIES, CLIENTS_TRACK_LIBRARY } from '../../common/constants/routingConstants'
import { getLyraTherapyContentsData } from '../../data/lyraTherapy/contentSelectors'
import { RootState } from '../../data/store'
import { useAssignCurriculumMutation, useUpdateCurriculumMutation } from '../episodes/data/episodesApi'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'
import MiniBackNav from '../miniBackNav/MiniBackNav'

type ConfigTrackContainerProps = {
  contents: Content[]
}

const ConfigTrackContainer: React.FC<ConfigTrackContainerProps> = ({ contents }) => {
  const location = useLocation()
  const sessionCount = location?.state?.sessionCount
  const track = location?.state?.track
  const navigate = useNavigate()
  const curriculum = track?.curriculumSchedule || []
  const [showingPreview, setShowingPreview] = useState(false)
  const { data: clientDetailsSelectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const selectedEpisode = clientDetailsSelectedEpisode
  const [updateCurriculum] = useUpdateCurriculumMutation()
  const [assignCurriculum] = useAssignCurriculumMutation()

  const originalElectiveTitles = curriculum.reduce((electives: string[], session: TrackSession) => {
    return electives.concat([isEmpty(session.contentOptions) ? session.title : ''])
  }, [])

  const [selectedElectives, setSelectedElectives] = useState(
    curriculum.reduce((electives: { name: string; title: string }[], session: TrackSession) => {
      return electives.concat([
        {
          name: isEmpty(session.contentOptions) ? session.name : 'selectElective',
          title: isEmpty(session.contentOptions) ? session.title : 'Select elective',
        },
      ])
    }, []),
  )
  const [modalContent, setModalContent] = useState<{ type: string | null; body: JSX.Element } | null>(null)
  const [isSubmittingTrack, setIsSubmittingTrack] = useState(false)

  const areElectivesSet = () => {
    return !selectedElectives
      .reduce((names: string[], elective: { name: string; title: string }) => {
        return names.concat(elective.name)
      }, [])
      .includes('selectElective')
  }

  const setElectiveInformation = (elective: { name: string; title: string }, index: number) => {
    curriculum[index].title = elective.title
    curriculum[index].name = elective.name
    curriculum[index].contents = curriculum[index].contentOptions[elective.name].contents
  }

  const [electivesChosen, setElectivesChosen] = useState(areElectivesSet)

  const showElectiveErrorModal = ({ title, body }: { title: string; body: string }) => {
    setModalContent({
      type: null,
      body: <ElectiveErrorContent title={title} body={body} closeFunc={() => setModalContent(null)} />,
    })
  }

  const previewContent = (assignment: Content) => {
    const selectedContent = contents.find((content) => content.name === assignment.name)
    if (selectedContent?.group === 'lesson') {
      setModalContent({
        type: 'lesson',
        body: (
          <Lesson
            onBackPress={() => setModalContent(null)}
            contentMetaData={selectedContent.meta_data!}
            content={selectedContent}
            assignmentId={selectedContent.id}
            instructions={selectedContent.instructions}
          />
        ),
      })
    } else if (selectedContent?.group === 'infosheet') {
      setModalContent({
        type: null,
        body: (
          <InfoSheet
            metaData={selectedContent.meta_data ?? {}}
            instructions={selectedContent.instructions ?? null}
            title={selectedContent.title}
            onLinkClicked={(url) => Linking.openURL(url)}
          />
        ),
      })
    } else {
      setShowingPreview(true)
      setModalContent({
        type: null,
        body: (
          <ActivityEntry
            contentMetadata={selectedContent?.meta_data}
            instructions={selectedContent?.instructions ?? ''}
            title={selectedContent?.title}
            name={selectedContent?.name}
            exit={() => {
              setModalContent(null)
              setShowingPreview(false)
            }}
            deleteDraft={noop}
            saveForm={noop}
            focused={false}
            withPageBreaks={false}
            isProviderPreview
            showAllPages
          />
        ),
      })
    }
  }

  const handleBackClicked = () => {
    navigate(CLIENTS_TRACK_LIBRARY.route, { state: { sessionCount: sessionCount } })
  }

  const dropdownOptions = curriculum.map((session: TrackSession, index: number) =>
    session.contentOptions
      ? Object.values(session.contentOptions).reduce(
          (options, content) =>
            options.concat({
              // @ts-expect-error TS(2769): No overload matches this call.
              id: content.name,
              text: content.title,
              selectHandler: () => {
                const newElectives = selectedElectives
                newElectives[index].name = content.name
                newElectives[index].title = content.title
                setSelectedElectives([...newElectives])
                setElectivesChosen(areElectivesSet)
                setElectiveInformation({ name: content.name, title: content.title }, index)
              },
            }),
          [],
        )
      : [],
  )

  const assignTrack = () => {
    return updateCurriculum({ id: selectedEpisode!.id, curriculum })
      .unwrap()
      .then(() => assignCurriculum({ id: selectedEpisode!.id }).unwrap())
      .then(() => navigate(CLIENT_ACTIVITIES.route))
  }

  const submitElectives = () => {
    if (!electivesChosen) {
      showElectiveErrorModal({
        title: 'Please choose additional activities.',
        body: 'Additional activities are required as part of the care plan.',
      })
    } else if (new Set(selectedElectives.map(({ name }: { name: string }) => name)).size !== selectedElectives.length) {
      // check that no electives are repeated
      showElectiveErrorModal({
        title: 'Please choose unique additional activities.',
        body: 'Additional activities are required as part of the curriculum track. They must be unique, so please ensure you haven’t added the same content to weeks 4 and 6.',
      })
    } else {
      setIsSubmittingTrack(true)
      assignTrack().finally(() => setIsSubmittingTrack(false))
    }
  }

  const isFullScreenOverlayOpen = modalContent?.type === 'lesson' || showingPreview
  return (
    <View>
      <div className={styles.container}>
        <MiniBackNav backFunc={handleBackClicked} subText={`Share new content`} />
        <div style={{ width: '100%' }}>
          <div className={styles['header-container']}>
            <h2>{track?.title}</h2>
            <DefaultButton
              onClick={submitElectives}
              isLoading={isSubmittingTrack}
              data-test-id='ConfigTrackContainer-addTrackButton'
            >
              Add Care Plan
            </DefaultButton>
          </div>
          {curriculum.map((session: TrackSession, index: number) => (
            <div
              className={styles['week-container']}
              key={index}
              data-test-id={`ConfigTrackContainer-week${index + 1}-container`}
            >
              <div className={styles['week-list-item']}>
                <div
                  className={styles['week-header-container']}
                  data-test-id={`ConfigTrackContainer-week${index + 1}-header`}
                >
                  <div className={styles['title-container']} data-test-id='ConfigTrackContainer-weekHeader'>
                    Week {index + 1} - {originalElectiveTitles[index] || 'Required elective'}
                  </div>
                  {!originalElectiveTitles[index] && (
                    <div
                      className={styles['dropdown-container']}
                      data-testid={`ConfigTrackContainer-elective-dropdown-${index}`}
                    >
                      <DropdownButton
                        dropdownItems={dropdownOptions[index]}
                        testID={tID(`ConfigTrackContainer-dropdownButton-${selectedElectives[index].title}`)}
                        buttonType={ButtonType.TERTIARY}
                        dropdownWidth={300}
                        fullWidth
                        iconAtEnd
                        disableCenteredContent
                        style={{
                          backgroundColor: 'transparent',
                          paddingHorizontal: 0,
                          height: 'auto',
                          minWidth: 250,
                        }}
                        contentContainerStyle={{ justifyContent: 'space-between' }}
                        selectedItemStyle={null}
                      >
                        <span
                          className={classNames(
                            selectedElectives[index].name === 'selectElective'
                              ? styles['track-placeholder']
                              : styles['track-select'],
                          )}
                        >
                          {selectedElectives[index].title}
                        </span>
                      </DropdownButton>
                    </div>
                  )}
                </div>
              </div>
              {session.contents &&
                Object.values(session.contents).map((content) => (
                  <div
                    className={classNames(styles['week-list-item'], styles['clickable-item'])}
                    onClick={() => previewContent(content)}
                    onKeyDown={() => previewContent(content)}
                    data-test-id={`ConfigTrackContainer-week${index + 1}-${content.name}Content`}
                    key={content.name}
                    role='none'
                  >
                    <div className={styles.name}>
                      <div className={styles.icon}>
                        <LtIcon type={content.group} />
                      </div>
                      {content.title}
                    </div>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>
      {isFullScreenOverlayOpen ? (
        <FullScreenOverlay isFocused={false} isOpen={isFullScreenOverlayOpen} renderInPortal>
          {modalContent?.body}
        </FullScreenOverlay>
      ) : (
        <BaseModal
          data-test-id='ConfigTrackContainer-modal'
          isOpen={!isNull(modalContent)}
          body={modalContent?.body ?? <div />}
          closeModal={() => setModalContent(null)}
        />
      )}
    </View>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    contents: getLyraTherapyContentsData(state),
  }
}

export default connect(mapStateToProps)(ConfigTrackContainer)
